/**
 * InboxClarifyModal building blocks — types, target builder, field sections.
 * Extracted from inbox-clarify-modal.tsx (behavior-preserving refactor).
 * The modal shell + submit logic stays in inbox-clarify-modal.tsx and imports these.
 */
import { todayIso } from "../lib/date";
import type { InboxConversionTarget, InboxConversionType, InboxItem } from "../types";
import { BentoColorInput, BentoDateInput, BentoSelect, BentoTimeInput } from "./bento-ui";

// ── Types ────────────────────────────────────────────────────────────────────

/** Optional pre-fill from NL quick-add parser (Phase 06) + AI capture (Phase 2.1). */
export interface ClarifyPrefill {
  title?: string;
  dueDate?: string;
  time?: string;
  priority?: "low" | "medium" | "high";
  tags?: string[];
  // Phase 2.1 AI capture — richer per-type fields (regex parser leaves these unset):
  estimateMinutes?: number;               // task
  horizon?: "quarter" | "year" | "life";  // goal: tầm nhìn
  metric?: string;                        // goal: chỉ số đo lường
  target?: number;                        // goal: số mục tiêu
}

/** A7: Queue context — passed from InboxView when bulk-clarify is active. */
export interface ClarifyQueueContext {
  /** 1-based position of this item in the queue (for display: "Mục 2/5"). */
  index: number;
  /** Total items in the queue. */
  total: number;
  /** Advance to the next item (called after convert OR skip). */
  onNext: () => void;
}

export type TypeMeta = { value: InboxConversionType; label: string; color: string };

export const TYPES: TypeMeta[] = [
  { value: "task",    label: "Việc",      color: "brand" },
  { value: "event",   label: "Lịch",      color: "amber" },
  { value: "project", label: "Dự án",     color: "sky" },
  { value: "goal",    label: "Mục tiêu",  color: "violet" },
  { value: "note",    label: "Ghi chú",   color: "emerald" },
  { value: "habit",   label: "Thói quen", color: "rose" },
];

// ── Hint-based default type (KISS: keyword scan on hint/text) ────────────────
export function guessType(item: InboxItem): InboxConversionType {
  const haystack = `${item.hint ?? ""} ${item.text}`.toLowerCase();
  if (/ý tưởng|note|ghi chú|đọc|tham khảo/.test(haystack)) return "note";
  if (/dự án|project|kế hoạch/.test(haystack)) return "project";
  if (/mục tiêu|goal|target|chỉ tiêu/.test(haystack)) return "goal";
  if (/thói quen|habit|hằng ngày|mỗi ngày|luyện tập/.test(haystack)) return "habit";
  if (/họp|hẹn|meeting|sự kiện|event/.test(haystack)) return "event";
  return "task";
}

// ── Build InboxConversionTarget from form state ──────────────────────────────
export function buildTarget(
  type: InboxConversionType,
  f: Record<string, string>,
): InboxConversionTarget | null {
  const title = f.title?.trim();
  if (!title) return null;

  if (type === "task") {
    const tags = f.tags ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : [];
    return {
      type: "task",
      item: {
        title,
        dueDate: f.dueDate || todayIso(),
        status: "todo",
        priority: (f.priority as "low" | "medium" | "high") || "medium",
        estimateMinutes: Number(f.estimateMinutes) || 30,
        subtasks: [],
        ...(tags.length ? { tags } : {}),
      },
    };
  }
  if (type === "event") {
    // P3-4: optional recurrence → emit a minimal RRule the calendar can expand.
    const RRULE: Record<string, string> = { daily: "FREQ=DAILY", weekly: "FREQ=WEEKLY", monthly: "FREQ=MONTHLY" };
    const rrule = RRULE[f.recurrence ?? "none"];
    return {
      type: "event",
      item: {
        title,
        date: f.dueDate || todayIso(),
        time: f.time || "09:00",
        ...(rrule ? { rrule } : {}),
      },
    };
  }
  if (type === "project") {
    const name = f.title?.trim();
    if (!name) return null;
    return {
      type: "project",
      item: {
        name,
        area: f.area?.trim() || "Chung",
        status: "active",
        deadline: f.deadline || todayIso(),
        progress: 0,
        nextAction: f.nextAction?.trim() || "",
      },
    };
  }
  if (type === "goal") {
    return {
      type: "goal",
      item: {
        title,
        horizon: (f.horizon as "quarter" | "year" | "life") || "quarter",
        metric: f.metric?.trim() || "",
        current: 0,
        target: Number(f.target) || 100,
        deadline: f.deadline || todayIso(),
        status: "active",
      },
    };
  }
  if (type === "habit") {
    // Clamp targetPerWeek to 1–7 (D19 edge case)
    const raw = Number(f.targetPerWeek) || 5;
    const targetPerWeek = Math.min(7, Math.max(1, Math.round(raw)));
    return {
      type: "habit",
      item: {
        name: title,
        targetPerWeek,
        color: f.habitColor || "#7c3aed",
      },
    };
  }
  // note
  return {
    type: "note",
    item: {
      title: f.title?.trim().slice(0, 100) || "Ghi chú",
      tags: f.tags ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      body: f.body?.trim() || "",
      pinned: false,
      attachments: [],
    },
  };
}

// ── Field helpers (local — không import entity-form) ────────────────────────
export function LabelInput({ label, id, value, onChange, type = "text", placeholder = "" }: {
  label: string; id: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string;
}) {
  if (type === "date") {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-[12px] font-semibold text-ink-600">{label}</label>
        <BentoDateInput
          id={id}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-xl border border-ink-200 bg-ink-50 px-3 py-2 text-[13px] outline-none focus:border-brand-400 focus:bg-white"
        />
      </div>
    );
  }
  if (type === "time") {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-[12px] font-semibold text-ink-600">{label}</label>
        <BentoTimeInput
          id={id}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-xl border border-ink-200 bg-ink-50 px-3 py-2 text-[13px] outline-none focus:border-brand-400 focus:bg-white"
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[12px] font-semibold text-ink-600">{label}</label>
      <input id={id} type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-ink-200 bg-ink-50 px-3 py-2 text-[13px] outline-none focus:border-brand-400 focus:bg-white" />
    </div>
  );
}

export function LabelSelect({ label, id, value, onChange, options }: {
  label: string; id: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[12px] font-semibold text-ink-600">{label}</label>
      {/* BentoSelect forwards id to the inner <select>; htmlFor still resolves correctly */}
      <BentoSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </BentoSelect>
    </div>
  );
}

// ── Per-type fields ──────────────────────────────────────────────────────────
export function TaskFields({ f, set }: { f: Record<string, string>; set: (k: string, v: string) => void }) {
  return (
    <>
      <LabelInput label="Hạn chót" id="dueDate" value={f.dueDate} onChange={(v) => set("dueDate", v)} type="date" />
      <LabelSelect label="Ưu tiên" id="priority" value={f.priority} onChange={(v) => set("priority", v)}
        options={[{ value: "high", label: "Cao" }, { value: "medium", label: "Trung bình" }, { value: "low", label: "Thấp" }]} />
      <LabelInput label="Thời lượng (phút)" id="estimateMinutes" value={f.estimateMinutes} onChange={(v) => set("estimateMinutes", v)} type="number" placeholder="30" />
      <LabelInput label="Tags (cách nhau bởi dấu phẩy)" id="tags" value={f.tags} onChange={(v) => set("tags", v)} placeholder="work, deep-focus" />
    </>
  );
}

/** Event fields — date + time. Pre-filled from NL parser ("3pm" → 15:00). */
export function EventFields({ f, set }: { f: Record<string, string>; set: (k: string, v: string) => void }) {
  return (
    <>
      <LabelInput label="Ngày" id="dueDate" value={f.dueDate} onChange={(v) => set("dueDate", v)} type="date" />
      <LabelInput label="Giờ" id="time" value={f.time} onChange={(v) => set("time", v)} type="time" />
      {/* P3-4: tạo luật lặp ngay khi phân loại (vd "đi tập gym mỗi chiều"). */}
      <LabelSelect
        label="Lặp lại"
        id="recurrence"
        value={f.recurrence ?? "none"}
        onChange={(v) => set("recurrence", v)}
        options={[
          { value: "none", label: "Không lặp" },
          { value: "daily", label: "Hằng ngày" },
          { value: "weekly", label: "Hằng tuần" },
          { value: "monthly", label: "Hằng tháng" },
        ]}
      />
    </>
  );
}

export function ProjectFields({ f, set }: { f: Record<string, string>; set: (k: string, v: string) => void }) {
  return (
    <>
      <LabelInput label="Khu vực" id="area" value={f.area} onChange={(v) => set("area", v)} placeholder="Chung" />
      <LabelInput label="Deadline" id="deadline" value={f.deadline} onChange={(v) => set("deadline", v)} type="date" />
      <LabelInput label="Bước tiếp theo" id="nextAction" value={f.nextAction} onChange={(v) => set("nextAction", v)} placeholder="Bước đầu tiên…" />
    </>
  );
}

export function GoalFields({ f, set }: { f: Record<string, string>; set: (k: string, v: string) => void }) {
  return (
    <>
      <LabelSelect label="Tầm nhìn" id="horizon" value={f.horizon} onChange={(v) => set("horizon", v)}
        options={[{ value: "quarter", label: "Quý" }, { value: "year", label: "Năm" }, { value: "life", label: "Suốt đời" }]} />
      <LabelInput label="Chỉ số đo lường" id="metric" value={f.metric} onChange={(v) => set("metric", v)} placeholder="VD: 10 km chạy/tuần" />
      <LabelInput label="Mục tiêu (số)" id="target" value={f.target} onChange={(v) => set("target", v)} type="number" placeholder="100" />
      <LabelInput label="Deadline" id="deadline" value={f.deadline} onChange={(v) => set("deadline", v)} type="date" />
    </>
  );
}

/**
 * D19 Habit fields — name pre-filled from title; targetPerWeek 1–7; color picker.
 * title is shared with the common "Tiêu đề" field above, so only extra fields here.
 */
export function HabitFields({ f, set }: { f: Record<string, string>; set: (k: string, v: string) => void }) {
  return (
    <>
      <div className="flex flex-col gap-1">
        <label htmlFor="targetPerWeek" className="text-[12px] font-semibold text-ink-600">
          Mục tiêu mỗi tuần (1–7 ngày)
        </label>
        <input
          id="targetPerWeek"
          type="number"
          min={1}
          max={7}
          value={f.targetPerWeek}
          onChange={(e) => {
            // Clamp in the input itself so user sees immediate feedback
            const v = Math.min(7, Math.max(1, parseInt(e.target.value, 10) || 1));
            set("targetPerWeek", String(v));
          }}
          className="rounded-xl border border-ink-200 bg-ink-50 px-3 py-2 text-[13px] outline-none focus:border-brand-400 focus:bg-white"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="habitColor" className="text-[12px] font-semibold text-ink-600">Màu sắc</label>
        <BentoColorInput
          id="habitColor"
          value={f.habitColor}
          onChange={(e) => set("habitColor", e.target.value)}
          className="h-10 bg-ink-50 px-3 py-2 text-[13px] focus:border-brand-400 focus:bg-white"
        />
      </div>
    </>
  );
}

export function NoteFields({ f, set }: { f: Record<string, string>; set: (k: string, v: string) => void }) {
  return (
    <>
      <div className="flex flex-col gap-1">
        <label htmlFor="body" className="text-[12px] font-semibold text-ink-600">Nội dung</label>
        <textarea id="body" value={f.body} rows={3} onChange={(e) => set("body", e.target.value)}
          className="rounded-xl border border-ink-200 bg-ink-50 px-3 py-2 text-[13px] outline-none focus:border-brand-400 focus:bg-white resize-none" />
      </div>
      <LabelInput label="Tags (cách nhau bởi dấu phẩy)" id="tags" value={f.tags} onChange={(v) => set("tags", v)} placeholder="đọc, ý tưởng" />
    </>
  );
}
